import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { CreateCustomerInput } from "@adva/types";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useCustomer, useUpdateCustomer } from "@/hooks/use-api";
import { CUSTOMER_FIELDS, customerToFormValues } from "./customer-form-config";

export default function V2CustomerEditPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const customerQuery = useCustomer(id);
  const updateCustomer = useUpdateCustomer();
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(values: CreateCustomerInput) {
    setError(null);
    try {
      await updateCustomer.mutateAsync({ id, input: values });
      toast.success("Customer updated");
      navigate(`/customers/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not save your changes — check the fields and try again.",
      );
    }
  }

  // GET /core/customers/:id returns a { customer: ... } wrapper (matching the
  // detail page); unwrap it before seeding the form so existing values load and
  // lifecycle_status isn't silently reset to "lead" on save (Codex review on
  // PR #1014).
  const customer = (
    customerQuery.data as { customer?: Record<string, unknown> } | undefined
  )?.customer;

  if (customerQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customerQuery.isError || !customer) {
    return (
      <EmptyState
        title="Customer not found"
        description="This customer may have been removed, or you don't have access to it."
        action={{
          label: "Back to customers",
          onClick: () => navigate("/customers"),
        }}
      />
    );
  }

  return (
    <EntityForm<CreateCustomerInput>
      title="Edit customer"
      breadcrumbs={[
        { label: "Customers", href: "/customers" },
        { label: "Edit" },
      ]}
      defaultValues={
        customerToFormValues(customer) as Partial<CreateCustomerInput>
      }
      fields={CUSTOMER_FIELDS}
      submitLabel="Save changes"
      onSubmit={onSubmit}
      cancelHref={`/customers/${id}`}
      error={error}
      isSubmitting={updateCustomer.isPending}
    />
  );
}
